import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild, Optional, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormArray, AbstractControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, firstValueFrom, map, Observable, startWith, Subject, take, takeUntil } from 'rxjs';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { MealRelation, MealRelationLabels } from 'src/app/common/enums/mealRelation-type';
import { MaterialModule } from 'src/app/material.module';
import { DosePatternDirective } from 'src/app/common/directive/dosePattern.directive';
import { DrugDetailComponent } from '../../settings/drug-tab/drug-list/drug-detail/drug-detail.component';
import { generateSuggestions, toNativeDate } from 'src/app/common/function-helper';
import { IPatientInfo } from '../../patient/ipatient-info';
import { IUsers } from '../../user/iusers';
import { Iopd, IOPDClinicalRecord } from '../opddetails/iopddetails';
import { IPrescription, IPrescriptionItem, PrescriptionType } from './iprescription';
import { DrugService } from '../../settings/drug-tab/drug-list/drug.service';
import { PrescriptionService } from './prescription.service';
import { PaginationResult } from 'src/app/models/common.response';
import { IDrugDetail } from '../../settings/drug-tab/drug-list/idrug';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { GenderList } from 'src/app/common/types/common';
import * as constants from 'src/app/common/constant/constants.type'
import { OPDService } from '../opd.service';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { environment } from 'src/environments/environment';
import { PrescriptionTemplateComponent } from '../../dialogs/prescription-template/prescription-template.component';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { PrescriptionTemplateService } from '../../dialogs/prescription-template/prescription-template.service';
import { NgScrollbarModule } from "ngx-scrollbar";
import { ConfirmationDialog } from '../../dialogs/confirmation/confirmation-dialog.component';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Iipd } from '../../ipd/Iipd.type';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { TemplateListComponent } from '../../template/template-list/template-list.component';
import { TemplateType } from 'src/app/common/enums/template-type';
import { PatientNameUtils } from 'src/app/common/utils/patient-name.utils';
import { FollowUpSlotPickerComponent, FollowUpSlotValue } from '../../appointment/follow-up-slot-picker/follow-up-slot-picker.component';
import { TimeConvertPipe } from 'src/app/pipe/time-convert.pipe';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';

@Component({
  selector: 'app-prescription',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    ReactiveFormsModule,
    TablerIconsModule,
    TranslateModule,
    NgScrollbarModule,
    DosePatternDirective,
    FollowUpSlotPickerComponent
  ],
  providers: [DatePipe, DefaultValuePipe, TimeConvertPipe, TimeFormatPipe],
  templateUrl: './prescription.component.html',
  styleUrl: './prescription.component.scss'
})
export class PrescriptionComponent implements OnInit, OnChanges, OnDestroy {

  // --------------------------------------------
  // TABLE & DISPLAY SETTINGS
  // --------------------------------------------
  displayedColumns: string[] = [
    'drugName',
    'drugPattern',
    'duration',
    'mealRelation',
    'instruction',
    'action'
  ];
  dataSource = new MatTableDataSource<IPrescriptionItem>();

  @Input() patientData!: IPatientInfo;
  @Input() doctorData!: IUsers;
  @Input() opdDeatils!: Iopd | null;
  @Input() ipdData?: Iipd | null;
  @Input() embedded: boolean = false;
  @Input() readOnly: boolean = false;
  @Output() loadingChange = new EventEmitter<boolean>();

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatAutocompleteTrigger) templateAutoTrigger!: MatAutocompleteTrigger;
  @ViewChild(FollowUpSlotPickerComponent) followUpSlotPicker?: FollowUpSlotPickerComponent;
  isDialog: boolean = false;
  prescriptionForm!: FormGroup;
  prescriptionItems: IPrescriptionItem[] = [];
  listofDrug: IDrugDetail[] = [];
  mealRelationList = Object.values(MealRelation).filter(x => typeof x === 'number');
  mealRelationData = MealRelationLabels;
  searchControl = new FormControl('');
  pagination!: PaginationResult;
  isEditMode: boolean = false;
  isLoading: boolean = true;
  private destroy$ = new Subject<void>();
  hospitalInfo: IHospitalInfo;
  staticimagepath = environment.imagePath;
  templateCtrl = new FormControl('');
  filteredTemplates: CommonDropDown[] = [];
  isTemplateApplied: boolean = false;
  selectedTemplates: CommonDropDown[] = [];

  // Inline editing properties
  prescriptionRows: FormArray<FormGroup>;
  rowFilteredDrugs$: Observable<IDrugDetail[]>[] = [];
  dosePatternSuggestions: string[][] = [];
  durationSuggestions: string[][] = [];
  hasUnsavedChanges: boolean = false;
  private suppressChangeTracking: boolean = false;
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  /** Cached value from the follow-up slot picker (OPD only) */
  followUpSlotValue: FollowUpSlotValue = { appointDate: null, appointTime: null };
  followUpInitialValue: FollowUpSlotValue | null = null;

  onFollowUpValueChange(value: FollowUpSlotValue): void {
    this.followUpSlotValue = value;
  }

  get followUpPickerDoctorId(): number | null {
    return this.doctorData?.id || this.opdDeatils?.doctorId || null;
  }

  get isOpdPrescription(): boolean {
    return !(this.prescriptionForm?.get('ipdId')?.value);
  }

  private setLoading(value: boolean): void {
    this.isLoading = value;
    this.loadingChange.emit(value);
  }

  private getPrescriptionDoctorId(): number {
    return Number(
      this.prescriptionForm?.get('doctorId')?.value ||
      this.doctorData?.id ||
      this.ipdData?.doctorId ||
      this.opdDeatils?.doctorId ||
      0
    );
  }

  private getTemplateSearchText(): string | undefined {
    const value = this.templateCtrl.value;
    return typeof value === 'string' && value.length >= 2 ? value : undefined;
  }

  constructor(
    private _drugService: DrugService,
    private _prescriptionService: PrescriptionService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private http: HttpClient,
    private hospitalData: HospitalStateService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private _opdService: OPDService,
    private translationUtils: TranslationUtils,
    private _lookupService: LookupService,
    private _prescriptionTemplateService: PrescriptionTemplateService,
    private defaultValuePipe: DefaultValuePipe,
    private printSettingService: PrintSettingService,
    private _timeConvertPipe: TimeConvertPipe,
    private timeFormatPipe: TimeFormatPipe,
    @Optional() private dialogRef: MatDialogRef<PrescriptionComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      patientDetails?: IPatientInfo,
      opdDetails?: Iopd,
      ipdId?: number,
      ipdData?: Iipd,
      doctorData?: IUsers,
      isDialog?: boolean
    },
  ) { }

  // --------------------------------------------
  // INIT
  // --------------------------------------------
  ngOnInit(): void {

    if (this.data?.isDialog) {
      this.isDialog = true;
    }

    // If inputs are not provided (e.g. opened via dialog), try to load from data
    if (!this.patientData && this.data?.patientDetails) {
      this.patientData = this.data.patientDetails;
    }
    if (!this.doctorData && this.data?.doctorData) {
      this.doctorData = this.data.doctorData;
    }
    if (!this.opdDeatils && this.data?.opdDetails) {
      this.opdDeatils = this.data.opdDetails;
    }
    if (!this.ipdData && this.data?.ipdData) {
      this.ipdData = this.data.ipdData;
    }

    const ipdId = this.data?.ipdId || this.ipdData?.id || null;

    this.prescriptionForm = this.fb.group({
      id: [0],
      prescriptionDate: [new Date(), Validators.required],
      doctorId: [this.getPrescriptionDoctorId(), Validators.required],
      patientId: [this.patientData?.id ?? 0, Validators.required],
      opdNumber: [this.opdDeatils?.opdNumber ?? '', Validators.required],
      opdId: [this.opdDeatils?.id ?? null, Validators.required],
      ipdId: [ipdId],
      ipdNumber: [this.ipdData?.ipdNumber ?? null],
      diagnosis: [''],
      prescriptionItems: [[]],
      treatmentAdviceOPD: ['']
    });

    // Initialize inline editing FormArray
    this.initializePrescriptionRows();
    this.initializeTemplateSearch();
    this.loadTemplateList();

    // Load drugs first, then load prescription data
    this.loadDrugs();
    this.loadFollowUpFromOpd();

    this.hospitalInfo = this.hospitalData.getHospital();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.prescriptionForm) {
      return;
    }

    const contextChanged = !!(changes['doctorData'] || changes['opdDeatils'] || changes['ipdData']);
    if (!contextChanged) {
      return;
    }

    const doctorId = this.getPrescriptionDoctorId();
    if (doctorId) {
      const doctorControl = this.prescriptionForm.get('doctorId');
      if (!doctorControl?.value || doctorControl.value !== doctorId) {
        doctorControl?.setValue(doctorId, { emitEvent: false });
      }
      this.loadTemplateList(this.getTemplateSearchText());
    }

    this.loadFollowUpFromOpd();
  }

  // --------------------------------------------
  // INLINE EDITING - FormArray Management
  // --------------------------------------------
  private initializePrescriptionRows(): void {
    this.prescriptionRows = this.fb.array<FormGroup>([]);
    this.rowFilteredDrugs$ = [];
    if (!this.readOnly) {
      this.withChangeTrackingSuppressed(() => this.addEmptyRow());
    }
    this.registerRowChangeTracking();
  }

  private registerRowChangeTracking(): void {
    this.prescriptionRows.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.suppressChangeTracking) {
          this.hasUnsavedChanges = true;
        }
      });

    this.prescriptionForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.suppressChangeTracking) {
          this.hasUnsavedChanges = true;
        }
      });
  }

  private withChangeTrackingSuppressed(fn: () => void): void {
    const previous = this.suppressChangeTracking;
    this.suppressChangeTracking = true;
    try {
      fn();
    } finally {
      this.suppressChangeTracking = previous;
    }
  }

  createPrescriptionRowGroup(item?: IPrescriptionItem): FormGroup {
    // Convert mealRelation string to number if needed (API returns string due to JsonStringEnumConverter)
    let mealRelationValue = item?.mealRelation ?? null;
    if (typeof mealRelationValue === 'string') {
      mealRelationValue = MealRelation[mealRelationValue as keyof typeof MealRelation] ?? null;
    }

    const row = this.fb.group({
      drugId: [item?.drugId ?? null, Validators.required],
      drugName: [item?.drugName ?? '', Validators.required],
      dosePattern: [item?.dosePattern ?? '', [Validators.required, Validators.maxLength(20)]],
      duration: [item?.duration ?? '', [Validators.required, Validators.maxLength(20)]],
      route: [item?.route ?? ''],           // Hidden but preserved
      indication: [item?.indication ?? ''], // Hidden but preserved
      mealRelation: [mealRelationValue],
      instruction: [item?.instruction ?? '']
    });

    if (this.readOnly) {
      row.disable({ emitEvent: false });
    }

    return row;
  }

  private setupRowDrugFilter(index: number): void {
    const row = this.prescriptionRows.at(index);
    this.rowFilteredDrugs$[index] = row.get('drugName')!.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      map(value => this.filterDrugsForRow(value, index))
    );
  }

  private filterDrugsForRow(value: string | IDrugDetail | null, rowIndex: number): IDrugDetail[] {
    const searchText = typeof value === 'string' ? value : (value?.name ?? '');
    const filterValue = searchText.toLowerCase();
    const selectedDrugIds = this.getSelectedDrugIdsExcluding(rowIndex);
    return this.listofDrug.filter(d =>
      d.name.toLowerCase().includes(filterValue) &&
      !selectedDrugIds.includes(d.id)
    );
  }

  private getSelectedDrugIdsExcluding(excludeIndex: number): number[] {
    return this.prescriptionRows.controls
      .filter((_, idx) => idx !== excludeIndex)
      .map(row => row.get('drugId')?.value)
      .filter((id): id is number => id != null);
  }

  addEmptyRow(): void {
    if (this.readOnly) {
      return;
    }

    const row = this.createPrescriptionRowGroup();
    this.prescriptionRows.push(row);
    const newIndex = this.prescriptionRows.length - 1;
    this.setupRowDrugFilter(newIndex);
    this.dosePatternSuggestions.push([]);
    this.durationSuggestions.push([]);
  }

  onDrugSelected(event: { option: { value: IDrugDetail } }, rowIndex: number): void {
    if (this.readOnly) return;

    const selected = event.option.value;
    if (!selected) return;

    const row = this.prescriptionRows.at(rowIndex);
    row.patchValue({
      drugId: selected.id,
      drugName: selected.name
    });
  }

  onRowComplete(rowIndex: number): void {
    if (this.readOnly) return;

    const row = this.prescriptionRows.at(rowIndex);
    if (this.isRowValid(row)) {
      this.syncRowToItems(rowIndex);

      // Add new empty row if this was the last row
      if (rowIndex === this.prescriptionRows.length - 1) {
        this.addEmptyRow();
      }
    }
  }

  private syncRowToItems(rowIndex: number): void {
    const row = this.prescriptionRows.at(rowIndex);
    const item = this.formGroupToItem(row);

    if (rowIndex < this.prescriptionItems.length) {
      this.prescriptionItems[rowIndex] = item;
    } else {
      this.prescriptionItems.push(item);
    }
    this.refreshTable();
  }

  private formGroupToItem(row: AbstractControl): IPrescriptionItem {
    const drugNameValue = row.get('drugName')?.value as string | IDrugDetail | null;
    return {
      drugId: row.get('drugId')?.value,
      drugName: typeof drugNameValue === 'string' ? drugNameValue : drugNameValue?.name ?? '',
      dosePattern: row.get('dosePattern')?.value,
      duration: row.get('duration')?.value,
      route: row.get('route')?.value,
      indication: row.get('indication')?.value,
      mealRelation: row.get('mealRelation')?.value,
      instruction: row.get('instruction')?.value
    };
  }

  onDeleteRow(index: number): void {
    if (this.readOnly) return;

    // If only one row, reset it instead of removing
    if (this.prescriptionRows.length <= 1) {
      this.prescriptionRows.at(0).reset();
      this.prescriptionItems = [];
      this.refreshTable();
      return;
    }

    this.prescriptionRows.removeAt(index);
    this.rowFilteredDrugs$.splice(index, 1);
    this.dosePatternSuggestions.splice(index, 1);
    this.durationSuggestions.splice(index, 1);

    if (index < this.prescriptionItems.length) {
      this.prescriptionItems.splice(index, 1);
    }
    this.refreshTable();
    this.refreshAllDrugFilters();
  }

  private refreshAllDrugFilters(): void {
    this.prescriptionRows.controls.forEach((row, index) => {
      const control = row.get('drugName');
      if (control) {
        // force valueChanges to emit
        control.setValue(control.value);
      }
    });
  }

  isLastRow(index: number): boolean {
    return index === this.prescriptionRows.length - 1;
  }

  isRowValid(row: AbstractControl): boolean {
    return row.get('drugId')?.value &&
      row.get('dosePattern')?.valid &&
      row.get('duration')?.valid;
  }

  isRowEmpty(row: AbstractControl): boolean {
    return !row.get('drugId')?.value &&
      !row.get('dosePattern')?.value &&
      !row.get('duration')?.value;
  }

  private hasRowContent(row: AbstractControl): boolean {
    const value = row.value ?? {};
    return !!(
      value.drugId ||
      value.drugName ||
      value.dosePattern ||
      value.duration ||
      (value.mealRelation !== null && value.mealRelation !== undefined && value.mealRelation !== '') ||
      value.instruction ||
      value.route ||
      value.indication
    );
  }

  // --------------------------------------------
  // ACTION VISIBILITY HELPERS (scalable for OPD/IPD)
  // --------------------------------------------
  get canPrint(): boolean {
    return this.hasSavedPrescription && this.prescriptionItems.length > 0;
  }

  get canSave(): boolean {
    return this.hasUnsavedChanges;
  }

  get canAddTemplate(): boolean {
    return this.prescriptionItems.length > 0;
  }

  get hasSavedPrescription(): boolean {
    return (this.prescriptionForm.get('id')?.value ?? 0) > 0;
  }

  get showActionButtons(): boolean {
    return this.canSave || this.canPrint || this.canAddTemplate;
  }

  get showEmptyState(): boolean {
    return this.prescriptionItems.length === 0 &&
      this.prescriptionRows.length === 1 &&
      this.isRowEmpty(this.prescriptionRows.controls[0]);
  }

  displayDrug(drug: IDrugDetail | string | null): string {
    return typeof drug === 'string' ? drug : drug?.name ?? '';
  }

  onInlineDurationInput(event: Event, index: number): void {
    if (this.readOnly) return;

    const value = (event.target as HTMLInputElement).value;
    this.durationSuggestions[index] = generateSuggestions(value);
  }

  openAddDrugDialogInline(rowIndex: number): void {
    if (this.readOnly) return;

    this.dialog
      .open(DrugDetailComponent, { width: '90vh', maxHeight: '90vh' })
      .afterClosed()
      .subscribe((res: { result?: IDrugDetail }) => {
        const newDrug = res?.result;
        if (!newDrug) return;

        this.listofDrug.push(newDrug);
        const row = this.prescriptionRows.at(rowIndex);
        row.patchValue({
          drugId: newDrug.id,
          drugName: newDrug.name
        });
      });
  }

  private rebuildFormArrayFromItems(markDirty: boolean = false): void {
    const rebuild = () => {
      this.prescriptionRows.clear();
      this.rowFilteredDrugs$ = [];
      this.dosePatternSuggestions = [];
      this.durationSuggestions = [];

      this.prescriptionItems.forEach((item, index) => {
        const row = this.createPrescriptionRowGroup(item);
        this.prescriptionRows.push(row);
        this.setupRowDrugFilter(index);
        this.dosePatternSuggestions.push([]);
        this.durationSuggestions.push([]);
      });

      if (!this.readOnly) {
        this.addEmptyRow();
      }
    };

    if (markDirty) {
      rebuild();
      this.hasUnsavedChanges = true;
    } else {
      this.withChangeTrackingSuppressed(rebuild);
      this.hasUnsavedChanges = false;
    }
  }

  // --------------------------------------------
  // LOAD DATA
  // --------------------------------------------
  loadDrugs() {
    this._drugService.getDrugDetails().subscribe(resp => {
      this.listofDrug = resp.result ?? [];

      const ipdId = this.prescriptionForm.get('ipdId')?.value;

      // Load prescription data
      if (ipdId) {
        this.loadPrescriptionDetailsByIPDId(ipdId);
        // Load clinical record if OPD details exist
        if (this.opdDeatils?.id) {
          this.loadOPDClinical(this.opdDeatils.id);
        }
      } else if (this.opdDeatils?.id) {
        this.loadPrescriptionDetailsByOpdId(this.opdDeatils.id);
        this.loadOPDClinical(this.opdDeatils.id);
      } else {
        this.setLoading(false);
      }
    });
  }

  loadPrescriptionDetailsByOpdId(opdId: number) {
    this._prescriptionService.getPrescriptionDetailByOpdId(opdId)
      .subscribe((response) => {

        const data = response.result;

        // If the prescription has an IPD ID, it belongs to IPD.
        // We should NOT load it in the OPD view.
        if (data && data.ipdId > 0) {
          return;
        }

        if (data) {
          this.prescriptionForm.patchValue({
            id: data.id,
            diagnosis: data.diagnosis,
            treatmentAdviceOPD: data.treatmentAdviceOPD || this.opdDeatils?.treatmentAdvice || '',
            prescriptionDate: data.prescriptionDate
          });
        } else {
          this.prescriptionForm.patchValue({
            treatmentAdviceOPD: this.opdDeatils?.treatmentAdvice || ''
          });
        }

        this.prescriptionItems = [];

        if (data?.prescriptionItems) {
          this.prescriptionItems = data.prescriptionItems.map((item: IPrescriptionItem) => {
            return {
              ...item,
              drugName: this.getDrugName(item.drugId),
            };
          });
        }

        this.refreshTable();

        // Rebuild FormArray for inline editing
        this.rebuildFormArrayFromItems(false);
        this.setLoading(false);
      });
  }

  loadPrescriptionDetailsByIPDId(ipdId: number) {
    this._prescriptionService.getPrescriptionDetailByIPDId(ipdId)
      .subscribe((response) => {

        const data = response.result;

        if (data) {
          this.prescriptionForm.patchValue({
            id: data.id,
            diagnosis: data.diagnosis,
            prescriptionDate: data.prescriptionDate
          });
        }

        this.prescriptionItems = [];

        if (data?.prescriptionItems) {
          this.prescriptionItems = data.prescriptionItems.map((item: IPrescriptionItem) => {
            return {
              ...item,
              drugName: this.getDrugName(item.drugId),
            };
          });
        }

        this.refreshTable();

        // Rebuild FormArray for inline editing
        this.rebuildFormArrayFromItems(false);
        this.setLoading(false);
      });
  }

  // --------------------------------------------
  // CRUD — PRESCRIPTION ITEMS
  // --------------------------------------------
  onPrescriptionAdded(item: IPrescriptionItem) {
    this.prescriptionItems.push(item);
    this.refreshTable();
    this.hasUnsavedChanges = true;
  }

  onPrescriptionUpdated(event: { index: number, item: IPrescriptionItem }) {
    this.prescriptionItems[event.index] = event.item;
    this.refreshTable();
    this.hasUnsavedChanges = true;
  }

  onDrugAdded(newDrug: IDrugDetail) {
    const existingDrug = this.listofDrug.find(d => d.id === newDrug.id);
    if (!existingDrug) {
      this.listofDrug.push(newDrug);
    }
  }


  // --------------------------------------------
  // SAVE / UPDATE ENTIRE PRESCRIPTION
  // --------------------------------------------
  onSaveAndUpdate() {
    if (this.isOpdPrescription && this.hasPartialFollowUpSlotSelection()) {
      this.followUpSlotPicker?.markAllAsTouched();
      this.toastr.error(this.getFollowUpSlotRequiredMessage());
      return;
    }

    const itemsToSave = this.prescriptionRows.controls
      .map(row => this.formGroupToItem(row))
      .filter(item => item.drugId && item.dosePattern && item.duration); // Filter out empty/invalid rows

    const treatmentAdvice = this.prescriptionForm.get('treatmentAdviceOPD')?.value;

    if (itemsToSave.length === 0 && !treatmentAdvice) {
      const message = this.translate.instant('VALIDATION.ONE_PRESCRIPTION');
      this.toastr.error(message);
      return;
    }

    this.prescriptionForm.patchValue({
      prescriptionItems: itemsToSave
    });

    const prescriptionData: IPrescription = { ...this.prescriptionForm.value };
    this.applyFollowUpToPrescriptionPayload(prescriptionData);

    // If IPD → don't send treatment advice at all
    if (this.prescriptionForm.get('ipdId')?.value) {
      prescriptionData.prescriptionType = PrescriptionType.IPD;
      delete prescriptionData.treatmentAdviceOPD;
      delete prescriptionData.followUpDate;
      delete prescriptionData.followUpTime;
    }

    const apiCall = prescriptionData.id && prescriptionData.id > 0
      ? this._prescriptionService.updatePrescription(prescriptionData)   // UPDATE
      : this._prescriptionService.createPrescription(prescriptionData);  // CREATE

    apiCall.subscribe({
      next: (response) => {
        this.toastr.success(
          this.translationUtils.getTranslatedMessage(
            prescriptionData.id ? 'COMMON.UPDATE' : 'COMMON.SAVE',
            this.translate.instant('ENTITY.PRESCRIPTION')
          )
        );

        this.hasUnsavedChanges = false;

        if (!prescriptionData.id || prescriptionData.id === 0) {
          this.prescriptionForm.patchValue({ id: response.result.id });
        }
        this.isEditMode = false;

        this.syncOpdFollowUpFromPayload(prescriptionData);
      },
      error: () => {
        this.toastr.error(
          prescriptionData.id ? "Error updating prescription!" : "Error saving prescription!"
        );
      }
    });
  }

  getPayloadForParentSave(): { isValid: boolean; payload: IPrescription | null } {
    const rowsWithContent = this.prescriptionRows.controls
      .filter(row => this.hasRowContent(row));

    if (rowsWithContent.length === 0) {
      return { isValid: true, payload: null };
    }

    const hasInvalidRows = rowsWithContent.some(row => !this.isRowValid(row));
    if (hasInvalidRows || this.prescriptionForm.invalid) {
      rowsWithContent.forEach(row => row.markAllAsTouched());
      this.prescriptionForm.markAllAsTouched();
      this.toastr.error(this.translate.instant('VALIDATION.FILL_REQUIRED_FIELDS'));
      return { isValid: false, payload: null };
    }

    const itemsToSave = rowsWithContent.map(row => this.formGroupToItem(row));
    this.prescriptionForm.patchValue({
      prescriptionItems: itemsToSave,
      ipdId: this.prescriptionForm.get('ipdId')?.value || this.ipdData?.id,
      ipdNumber: this.prescriptionForm.get('ipdNumber')?.value || this.ipdData?.ipdNumber,
      opdId: this.prescriptionForm.get('opdId')?.value || this.opdDeatils?.id,
      opdNumber: this.prescriptionForm.get('opdNumber')?.value || this.opdDeatils?.opdNumber,
      patientId: this.prescriptionForm.get('patientId')?.value || this.patientData?.id,
      doctorId: this.getPrescriptionDoctorId(),
    });

    const payload: IPrescription = {
      ...this.prescriptionForm.value,
      prescriptionType: PrescriptionType.IPD,
    };
    delete payload.treatmentAdviceOPD;
    delete payload.followUpDate;
    delete payload.followUpTime;

    return { isValid: true, payload };
  }

  // --------------------------------------------
  // UTILITIES
  // --------------------------------------------
  private refreshTable() {
    this.dataSource.data = [...this.prescriptionItems];
    this.prescriptionForm.patchValue({ prescriptionItems: this.prescriptionItems });
  }

  getDrugName(drugId: number): string {
    const drug = this.listofDrug.find(x => x.id === drugId);
    return drug ? drug.name : 'Unknown Drug';
  }

  getMealRelationName(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';

    // If value is a string like "AfterFood", convert to enum number
    if (typeof value === 'string') {
      const enumValue = MealRelation[value as keyof typeof MealRelation];
      return MealRelationLabels[enumValue as MealRelation] ?? value;
    }

    // If value is already a number
    return MealRelationLabels[value as MealRelation] ?? '';
  }

  // --------------------------------------------
  // Print 
  // --------------------------------------------

  onPrintPrescription() {
    const ipdId = this.prescriptionForm.get('ipdId')?.value;
    const opdId = this.opdDeatils?.id;

    let prescription$: Observable<any>;

    if (ipdId && ipdId > 0) {
      prescription$ = this._prescriptionService.getPrescriptionDetailByIPDId(ipdId);
    } else if (opdId) {
      prescription$ = this._prescriptionService.getPrescriptionDetailByOpdId(opdId);
    } else {
      return; // Nothing to print
    }

    prescription$.subscribe({
      next: (response) => {
        this.printFromResponse(response);
      }
    });
  }

  private printFromResponse(response: any) {
    const data = response.result;
    if (!data) return;

    const items = data.prescriptionItems.map((p: IPrescriptionItem) => ({
      ...p,
      drugName: this.getDrugName(p.drugId)
    }));

    this.createPrescriptionPrintContent(items, data).then(content => {
      this.openPrintWindow(content);
    });
  }

  private async createPrescriptionPrintContent(items: IPrescriptionItem[], prescription: IPrescription): Promise<string> {
    let template =
      (await firstValueFrom(
        this.http.get('assets/print-template.html', { responseType: 'text' })
      )) || '';

    const hospitalSafe = this.hospitalData.getHospital();
    const settings = await this.printSettingService.getPrintSettingPromise(hospitalSafe.id);
    const patient = this.patientData;
    const doctor = this.doctorData;
    const opd = this.opdDeatils;
    const ipd = this.data?.ipdData;
    const isIPDPrescription = !!(this.prescriptionForm.get('ipdId')?.value || ipd);
    const nextVisitHtml = isIPDPrescription ? '' : this.buildNextVisitHtml(opd);

    const patientAgeGender = [
      this.getGenderName(patient?.gender),
      patient?.age
    ].filter(value => value !== null && value !== undefined && value !== '').join(' / ');

    const opdDate = this.datePipe.transform(opd?.opdDate, constants.DDMMYYYY_FORMAT) ?? '';
    const ipdDate = ipd?.ipdDate ? this.datePipe.transform(ipd.ipdDate, constants.DDMMYYYY_FORMAT) ?? '' : '';
    const encounterNumber = isIPDPrescription
      ? (ipd?.ipdNumber?.toString() ?? this.prescriptionForm.get('ipdNumber')?.value?.toString() ?? '')
      : (opd?.opdNumber ?? '');
    const encounterDate = isIPDPrescription ? ipdDate : opdDate;

    const rowsHtml = items
      .map(
        (p) => `
      <tr>
        <td style="padding:6px 10px; border:1px solid #ccc; text-align:left;">${this.getDrugName(p.drugId)}</td>
        <td style="padding:6px 10px; border:1px solid #ccc; text-align:left;">${p.dosePattern}</td>
        <td style="padding:6px 10px; border:1px solid #ccc; text-align:left;">${p.duration}</td>
        <td style="padding:6px 10px; border:1px solid #ccc; text-align:left;">${this.getMealRelationName(p.mealRelation)}</td>
        <td style="padding:6px 10px; border:1px solid #ccc; text-align:left; word-break:break-word; white-space:normal;">${p.instruction ?? ''}</td>
      </tr>`
      )
      .join('');

    const treatmentAdviceHtml = isIPDPrescription ? '' : `
    <div style="margin-top:20px; text-align:left;">
      <span style="font-size:16px; font-weight:500;">Treatment Advice : </span>
      <div style="margin-top:5px; font-size:14px; white-space: pre-wrap;">{{treatmentAdvice}}</div>
    </div>
  `;

    const tableHtml = `
    <img src="/assets/images/svgs/icon-rx.svg" height="30px" width="30px" style="vertical-align:middle;"/>
    <div style="margin-top:10px; margin-bottom:10px; text-align:left;">
      
      <span style="font-size:16px; font-weight:500; vertical-align:middle; margin-left:6px;">Diagnosis : {{diagnosis}}</span>
    </div>

    <table style="width:100%; border-collapse:collapse; margin-top:10px; border:1px solid #ccc; font-family: sans-serif; font-size:14px; text-align:left;">
      <thead>
        <tr style="background:#f2f2f2;">
          <th style="padding:8px 10px; border:1px solid #ccc; font-weight:600; width:25%;">Drug</th>
          <th style="padding:8px 10px; border:1px solid #ccc; font-weight:600; width:15%;">Dose Pattern</th>
          <th style="padding:8px 10px; border:1px solid #ccc; font-weight:600; width:15%;">Duration</th>
          <th style="padding:8px 10px; border:1px solid #ccc; font-weight:600; width:20%;">Meal Relation</th>
          <th style="padding:8px 10px; border:1px solid #ccc; font-weight:600; width:25%;">Instruction</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
    ${treatmentAdviceHtml}
    ${nextVisitHtml}
  `;

    const fullImagePath = hospitalSafe?.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
    const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

    const replacements: Record<string, string> = {
      reportTitle: isIPDPrescription ? 'IPD Prescription' : 'OPD Prescription',
      hospitalName: hospitalSafe.name ?? '',
      hospitalAddress: `${hospitalSafe.address ?? ''}, ${hospitalSafe.city ?? ''}`,
      phone: hospitalSafe.phone ?? '',
      reportSections: tableHtml,
      patientName: PatientNameUtils.formatFullName(patient),
      doctorName: `${doctor?.firstName ?? ''} ${doctor?.lastName ?? ''}`.trim(),
      qualification: doctor?.degree ?? '',
      patientAge: patientAgeGender,
      opdNumber: opd?.opdNumber ?? '',
      opdDate: opdDate,
      doctorRegistrationNo: doctor?.registrationNumber
        ? `Reg No. ${this.defaultValuePipe.transform(doctor?.registrationNumber, '')}`
        : '',
      doctorPhone: doctor?.phoneNumber
        ? `M. ${this.defaultValuePipe.transform(doctor?.phoneNumber, '')}`
        : '',
      diagnosis: this.prescriptionForm.get('diagnosis')?.value ?? '',
      treatmentAdvice: this.prescriptionForm.get('treatmentAdviceOPD')?.value ?? '',
      logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
      ipdNumber: ipd?.ipdNumber?.toString() || '',
      ipdDate: ipdDate
    };

    let filledTemplate = template;
    if (isIPDPrescription) {
      filledTemplate = filledTemplate
        .replace(/OPD No:/g, 'IPD No:')
        .replace(/OPD Date:/g, 'IPD Date:');
    }

    for (const key in replacements) {
      const value = replacements[key] ?? '';
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      filledTemplate = filledTemplate.replace(regex, value);
    }

    // Apply settings
    let printStyles = `
      #patientInfo { display: block !important; }
    `;

    if (settings) {
      const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
      const headerClass = settings.logoPosition === 'center' ? 'center' : '';
      const headerAlignment = settings.addressAlignment || 'left';

      printStyles = `
                    :root {
                        --logo-size: ${settings.logoSize || '60px'};
                        --logo-position: ${logoPos};
                        --header-alignment: ${headerAlignment};
                        --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                        --address-font-size: ${settings.addressFontSize || 13}px;
                        --footer-display: ${settings.showFooter ? 'block' : 'none'};
                        --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                        --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
                    }
                    #patientInfo { display: block !important; }
                `;
      filledTemplate = filledTemplate.replace('{{headerClass}}', headerClass);
    } else {
      filledTemplate = filledTemplate.replace('{{headerClass}}', '');
    }

    filledTemplate = filledTemplate.replace('</head>', `<style>${printStyles}</style></head>`);

    return filledTemplate;
  }

  private buildNextVisitHtml(opd: Iopd | null | undefined): string {
    if (!opd?.followUpDate && !opd?.followUpTime) {
      return '';
    }

    const followUpDate = opd.followUpDate
      ? this.datePipe.transform(opd.followUpDate, 'dd-MM-yyyy')
      : null;
    const followUpTime = opd.followUpTime
      ? this.timeFormatPipe.transform(opd.followUpTime)
      : null;
    const nextVisitOn = followUpDate && followUpTime
      ? `${followUpDate} at ${followUpTime}`
      : [followUpDate, followUpTime].filter(Boolean).join(' ');

    return nextVisitOn
      ? `<div style="margin-top:15px; font-size:13px;"><strong>Next Visit On:</strong> ${nextVisitOn}</div>`
      : '';
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }

  getGenderName(value?: string): string {
    const gender = GenderList.find(g => g.value === value);
    return gender ? gender.name : '';
  }

  // --------------------------------------------
  // DESTROY
  // --------------------------------------------
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadOPDClinical(opdId: number): void {
    this._opdService.getOPDClinical(opdId).subscribe((res) => {
      const items: IOPDClinicalRecord[] = res?.result || [];
      if (items.length === 0) return;

      const diagnosisList = items
        .map((record: IOPDClinicalRecord) => record.primaryDiagnosis?.trim() || '')
        .filter((diagnosis: string) => diagnosis !== '');

      const diagnosisText = diagnosisList.join(', ');

      this.prescriptionForm.patchValue({
        diagnosis: diagnosisText
      });
    });
  }

  initializeTemplateSearch() {

    this.templateCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe(value => {

        // textbox cleared → restore list
        if (!value) {
          this.loadTemplateList();
          return;
        }

        // run search
        if (typeof value === 'string' && value.length >= 2) {
          this.loadTemplateList(value);
        }
      });
  }

  private loadTemplateList(keyword?: string) {
    const doctorId = this.getPrescriptionDoctorId();
    if (!doctorId) {
      this.filteredTemplates = [];
      return;
    }

    this._lookupService.getPrescriptionTemplateName({
      doctorId,
      keyword: keyword || undefined
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.filteredTemplates = res?.result ?? [];
      });
  }

  OnPrescriptionTemplate() {
    // Create a deep copy to avoid mutating the original prescription data
    const prescriptionData: IPrescription = {
      ...this.prescriptionForm.value,
      prescriptionItems: this.prescriptionItems.map(item => ({ ...item }))
    };

    const dialogRef = this.dialog.open(PrescriptionTemplateComponent, {
      width: '520px',
      maxWidth: '90vw',
      data: {
        prescriptionItems: prescriptionData,
        isTemplateApplied: this.isTemplateApplied
      }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        // reload template dropdown list
        this.loadTemplateList();
        // reset search textbox
        this.templateCtrl.setValue('', { emitEvent: false });
        // allow selecting new template
        this.isTemplateApplied = false;
      }
    });
  }


  displayTemplateName(template: CommonDropDown | string | null): string {
    return typeof template === 'string' ? template : template?.name ?? '';
  }

  // for unique drugId & show warning for duplicates
  private getUniquePrescriptionItems(
    items: IPrescriptionItem[]
  ): IPrescriptionItem[] {

    const map = new Map<number, IPrescriptionItem>();
    const duplicateDrugNames: string[] = [];

    items.forEach(item => {
      if (map.has(item.drugId)) {
        duplicateDrugNames.push(
          item.drugName || this.getDrugName(item.drugId)
        );
      } else {
        map.set(item.drugId, item);
      }
    });

    // Inform user (OK-only dialog)
    if (duplicateDrugNames.length > 0) {
      const uniqueNames = [...new Set(duplicateDrugNames)].join(', ');

      this.dialog.open(ConfirmationDialog, {
        width: '450px',
        data: {
          title: this.translate.instant('ENTITY.SKIP_DRUG'),
          message: `${this.translate.instant('LABEL.SKIP_DRUG_MESSAGE')} <strong>${uniqueNames}</strong>`
        }
      }).afterClosed().subscribe(() => {
        this.templateAutoTrigger?.closePanel();
      })
    }
    // ALWAYS return unique items
    return Array.from(map.values());
  }


  onTemplateSelected(template: CommonDropDown) {
    if (!template?.id) {
      return;
    }

    this.templateAutoTrigger?.closePanel();
    this.applyTemplate(template);
    this.templateCtrl.setValue('', { emitEvent: false });
  }

  private applyTemplate(template: CommonDropDown) {

    this._prescriptionTemplateService.getPrescriptionTemplateById(template.id)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(response => {
        const templateDrugs = response?.result?.prescriptionTemplateDetails ?? [];

        const normalizedTemplateItems: IPrescriptionItem[] = templateDrugs.map((t: IPrescriptionItem) => ({
          prescriptionId: this.prescriptionForm.get('id')?.value || 0,
          drugId: t.drugId,
          dosePattern: t.dosePattern,
          duration: t.duration,
          route: t.route,
          indication: t.indication,
          mealRelation: t.mealRelation,
          instruction: t.instruction,
          drugName: t.drugName || this.getDrugName(t.drugId),
          isDeleted: false
        }));

        const merged = [
          ...this.prescriptionItems,
          ...normalizedTemplateItems
        ];

        this.prescriptionItems = this.getUniquePrescriptionItems(merged);
        this.refreshTable();
        this.rebuildFormArrayFromItems(true);
        this.isTemplateApplied = true;
      });
  }
  onCloseDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  openTemplatePopupforTreatmentAdvice(): void {
    const templateDialog = this.dialog.open(TemplateListComponent, {
      width: '60vw',
      maxWidth: '70vw',
      height: '60vh',
      maxHeight: '75vh',
      disableClose: true,
      data: {
        templateType: TemplateType.TreatmentAdvice
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result && result.selected && result.template) {
        // Get the current value of the treatment advice field
        const currentValue = this.prescriptionForm.get('treatmentAdviceOPD')?.value || '';
        const templateText = result.template.templateDescription || '';

        const parts = [currentValue.trim(), templateText.trim()].filter(Boolean);
        const combinedText = parts.join('\n');

        this.prescriptionForm.get('treatmentAdviceOPD')?.setValue(combinedText);
      }
    });
  }

  // --------------------------------------------
  // FOLLOW-UP (OPD only)
  // --------------------------------------------
  private loadFollowUpFromOpd(): void {
    if (!this.isOpdPrescription) {
      return;
    }

    if (!this.opdDeatils?.followUpDate || !this.opdDeatils?.followUpTime) {
      this.followUpInitialValue = null;
      this.followUpSlotValue = { appointDate: null, appointTime: null };
      return;
    }

    this.followUpInitialValue = {
      appointDate: toNativeDate(this.opdDeatils.followUpDate),
      appointTime: this.opdDeatils.followUpTime,
    };
    this.followUpSlotValue = this.followUpInitialValue;
  }

  private hasPartialFollowUpSlotSelection(): boolean {
    const { appointDate, appointTime } = this.followUpSlotValue;
    return (!!appointDate || !!appointTime) && (!appointDate || !appointTime);
  }

  private getFollowUpSlotRequiredMessage(): string {
    const key = 'APPOINTMENT_RELATED.FOLLOW_UP_SLOT_REQUIRED';
    const message = this.translate.instant(key);
    return message === key
      ? 'Please select both follow-up date and time, or clear the follow-up appointment.'
      : message;
  }

  private applyFollowUpToPrescriptionPayload(prescriptionData: IPrescription): void {
    const { appointDate, appointTime } = this.followUpSlotValue;
    const formattedDate = appointDate
      ? this.datePipe.transform(toNativeDate(appointDate), constants.DATE_FORMAT)
      : null;
    const formattedTime = appointTime
      ? this._timeConvertPipe.transform(appointTime)
      : null;

    prescriptionData.followUpDate = formattedDate;
    prescriptionData.followUpTime = formattedTime;
  }

  private syncOpdFollowUpFromPayload(prescriptionData: IPrescription): void {
    if (!this.isOpdPrescription || !this.opdDeatils) {
      return;
    }

    this.opdDeatils.followUpDate = prescriptionData.followUpDate ?? null;
    this.opdDeatils.followUpTime = prescriptionData.followUpTime ?? null;
    this.followUpInitialValue = {
      appointDate: toNativeDate(this.opdDeatils.followUpDate),
      appointTime: this.opdDeatils.followUpTime,
    };
    this.followUpSlotValue = this.followUpInitialValue;
  }

}
